import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { JobsiteProvider } from "@/context/JobsiteContext";

import ProtectedRoute from "@/components/layout/ProtectedRoute";
import AppShell from "@/components/layout/AppShell";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Join from "@/pages/Join";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import { Terms, NotFound } from "@/pages/Static";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import LaunchGate from "@/components/LaunchGate";
import Dashboard from "@/pages/Dashboard";
import DashboardMobile from "@/pages/DashboardMobile";
import Attendance from "@/pages/Attendance";
import AttendanceMobile from "@/pages/AttendanceMobile";
import useIsMobile from "@/lib/useIsMobile";
import Chat from "@/pages/Chat";
import Inspections from "@/pages/Inspections";
import InspectionsMobile from "@/pages/InspectionsMobile";
import DailyLogs from "@/pages/DailyLogs";
import Announcements from "@/pages/Announcements";
import MyShifts from "@/pages/MyShifts";
import Photos from "@/pages/Photos";
import ProjectInfo from "@/pages/ProjectInfo";
import Forms from "@/pages/Forms";
import AdminConsole from "@/pages/admin/AdminConsole";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminJobsites from "@/pages/admin/AdminJobsites";
import AdminTimeTracking from "@/pages/admin/AdminTimeTracking";
import AdminChatMonitor from "@/pages/admin/AdminChatMonitor";
import AdminITPTemplates from "@/pages/admin/AdminITPTemplates";
import AdminITPRegister from "@/pages/admin/AdminITPRegister";
import AdminRoster from "@/pages/admin/AdminRoster";
import AdminNCRs from "@/pages/admin/AdminNCRs";
import AdminCompany from "@/pages/admin/AdminCompany";
import AdminEmployees from "@/pages/admin/AdminEmployees";
import AdminForms from "@/pages/admin/AdminForms";
import FormsSupervisor from "@/pages/admin/FormsSupervisor";
import ChangePassword from "@/pages/ChangePassword";
import AcceptInvite from "@/pages/AcceptInvite";
import Pending from "@/pages/Pending";
import MasterCompanies from "@/pages/admin/MasterCompanies";

// Detecta se a pagina esta rodando dentro do app de iPhone (Capacitor).
const isNativeApp = () => {
  try {
    return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
  } catch (e) {
    return false;
  }
};

function LandingGate() {
  const { user } = useAuth();
  if (user && user !== false) return <Navigate to="/app" replace />;
  // Dentro do app de iPhone nao mostramos o site de apresentacao: vai direto pro login.
  if (isNativeApp()) return user === false ? <Navigate to="/login" replace /> : null;
  return <Landing />;
}


// No celular usa a tela de ponto nova (My Time); no computador continua a tela de sempre.
function ResponsiveAttendance() {
  const isMobile = useIsMobile();
  return isMobile ? <AttendanceMobile /> : <Attendance />;
}

// Tela inicial: no celular a Home nova (projeto, ponto, avisos e ferramentas); no computador a de sempre.
function ResponsiveDashboard() {
  const isMobile = useIsMobile();
  return isMobile ? <DashboardMobile /> : <Dashboard />;
}

// Inspections: no celular a tela nova (lista em cartoes, ITP com anexos e perguntas); no computador a de sempre.
function ResponsiveInspections() {
  const isMobile = useIsMobile();
  return isMobile ? <InspectionsMobile /> : <Inspections />;
}

function CompanyStatusGate({ children }) {
  const { user, company } = useAuth();
  if (!user || user === false) return children;
  if (user.is_master_admin) return children;
  const status = company?.status;
  if (status && status !== "active") {
    return <Navigate to="/app/pending" replace />;
  }
  return children;
}

function AdminGuard({ children }) {
  const { user } = useAuth();
  if (!user || (user.role !== "admin" && user.role !== "supervisor")) {
    return <Navigate to="/app" replace />;
  }
  return children;
}

function AdminOnlyGuard({ children }) {
  const { user } = useAuth();
  if (!user || user.role !== "admin") {
    return <Navigate to="/app" replace />;
  }
  return children;
}

function SupervisorGuard({ children }) {
  const { user } = useAuth();
  if (!user || (user.role !== "supervisor" && user.role !== "admin")) {
    return <Navigate to="/app" replace />;
  }
  return children;
}

function MasterGuard({ children }) {
  const { user } = useAuth();
  if (!user || !user.is_master_admin) {
    return <Navigate to="/app" replace />;
  }
  return children;
}

function Shell() {
  return (
    <JobsiteProvider>
      <AppShell>
        <Routes>
          <Route index element={<CompanyStatusGate><ResponsiveDashboard /></CompanyStatusGate>} />
          <Route path="attendance" element={<CompanyStatusGate><ResponsiveAttendance /></CompanyStatusGate>} />
          <Route path="chat" element={<CompanyStatusGate><Chat /></CompanyStatusGate>} />
          <Route path="inspections" element={<CompanyStatusGate><ResponsiveInspections /></CompanyStatusGate>} />
          <Route path="inspections/:id" element={<CompanyStatusGate><ResponsiveInspections /></CompanyStatusGate>} />
          <Route path="logs" element={<CompanyStatusGate><DailyLogs /></CompanyStatusGate>} />
          <Route path="announcements" element={<CompanyStatusGate><Announcements /></CompanyStatusGate>} />
          <Route path="my-shifts" element={<CompanyStatusGate><MyShifts /></CompanyStatusGate>} />
          <Route path="photos" element={<CompanyStatusGate><Photos /></CompanyStatusGate>} />
          <Route path="photos/:areaId" element={<CompanyStatusGate><Photos /></CompanyStatusGate>} />
          <Route path="project" element={<CompanyStatusGate><ProjectInfo /></CompanyStatusGate>} />
          <Route path="forms" element={<CompanyStatusGate><Forms /></CompanyStatusGate>} />
          
          {/* Admin Routes */}
          <Route path="admin" element={<AdminGuard><CompanyStatusGate><AdminConsole /></CompanyStatusGate></AdminGuard>} />
          <Route path="admin/users" element={<AdminGuard><CompanyStatusGate><AdminUsers /></CompanyStatusGate></AdminGuard>} />
          <Route path="admin/jobsites" element={<AdminGuard><CompanyStatusGate><AdminJobsites /></CompanyStatusGate></AdminGuard>} />
          <Route path="admin/roster" element={<AdminGuard><CompanyStatusGate><AdminRoster /></CompanyStatusGate></AdminGuard>} />
          <Route path="admin/time-tracking" element={<AdminGuard><CompanyStatusGate><AdminTimeTracking /></CompanyStatusGate></AdminGuard>} />
          <Route path="admin/chat-monitor" element={<AdminGuard><CompanyStatusGate><AdminChatMonitor /></CompanyStatusGate></AdminGuard>} />
          <Route path="admin/itp-templates" element={<AdminGuard><CompanyStatusGate><AdminITPTemplates /></CompanyStatusGate></AdminGuard>} />
          <Route path="admin/itp-register" element={<AdminGuard><CompanyStatusGate><AdminITPRegister /></CompanyStatusGate></AdminGuard>} />
          <Route path="admin/ncrs" element={<AdminGuard><CompanyStatusGate><AdminNCRs /></CompanyStatusGate></AdminGuard>} />
          <Route path="admin/company" element={<AdminGuard><CompanyStatusGate><AdminCompany /></CompanyStatusGate></AdminGuard>} />
          <Route path="admin/employees" element={<AdminGuard><CompanyStatusGate><AdminEmployees /></CompanyStatusGate></AdminGuard>} />
          
          {/* Forms Routes */}
          <Route path="admin/forms" element={<AdminOnlyGuard><CompanyStatusGate><AdminForms /></CompanyStatusGate></AdminOnlyGuard>} />
          <Route path="supervisor/forms" element={<SupervisorGuard><CompanyStatusGate><FormsSupervisor /></CompanyStatusGate></SupervisorGuard>} />
          
          {/* Master Admin */}
          <Route path="master" element={<MasterGuard><MasterCompanies /></MasterGuard>} />
          
          {/* Account */}
          <Route path="change-password" element={<ChangePassword />} />
          
          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </AppShell>
    </JobsiteProvider>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <LaunchGate>
          <Routes>
            <Route path="/" element={<LandingGate />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/join" element={<Join />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/accept-invite/:token" element={<AcceptInvite />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/app/pending" element={<ProtectedRoute><Pending /></ProtectedRoute>} />
            <Route path="/app/*" element={<ProtectedRoute><Shell /></ProtectedRoute>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </LaunchGate>
          <Toaster theme="system" position="top-right" richColors closeButton />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
